import { HeaderCTAButton } from '@/components/buttons/HeaderCTAButton';
import {
  MenuIcon,
  MoreIcon,
  ProjectsIcon,
  SumthingIcon,
  SupportersIcon,
  UpdatesIcon,
} from '@/components/icons';
import { AppLink } from '@/components/links/AppLink';
import { useManifest } from '@/contexts/ManifestContext';
import { Routes } from '@/routes';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';

interface MobileHeaderProps {
  slideMenuToggle: (value: boolean) => void;
}

const MobileHeader = ({ slideMenuToggle }: MobileHeaderProps) => {
  const { t } = useTranslation();
  const { features, header, type, mainSiteUrl } = useManifest();

  if (features.headerVariant === 'burger') {
    return (
      <div
        className="bg-neutral-white border-neutral-light sticky top-0 z-[80] flex w-full justify-center border-b"
        style={{ position: 'sticky' }}
      >
        {/* 50px content row hugged by the logo/CTA, with 12px top/bottom padding. */}
        <div className="flex w-full items-center justify-between px-5 py-3">
          <AppLink
            href={mainSiteUrl ?? Routes.Home}
            className="group inline-block"
            onClick={() => slideMenuToggle(false)}
          >
            <img
              src={header.logo.url}
              alt="logo"
              className="h-12.5 w-auto group-hover:opacity-90"
            />
          </AppLink>
          <div className="flex items-center gap-4">
            <HeaderCTAButton size="lg" onClick={() => slideMenuToggle(false)} />
            <button
              aria-label={t('layout.menu.more')}
              onClick={() => slideMenuToggle(true)}
              className={cn(
                'text-primary-alt flex h-12.5 shrink-0 items-center justify-center transition-opacity hover:cursor-pointer hover:opacity-80',
                // Sumthing uses a circular outlined button; white-labels (partner)
                // show just the bare icon.
                type !== 'partner' &&
                  'border-primary-alt w-12.5 rounded-full border-2',
              )}
            >
              <MenuIcon className="w-6" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    { label: t('layout.menu.impact'), Icon: UpdatesIcon, route: Routes.Impact },
    {
      label: t('layout.menu.projects'),
      Icon: ProjectsIcon,
      route: Routes.Projects,
    },
    {
      label: t('layout.menu.home'),
      Icon: SumthingIcon,
      route: Routes.Home,
      accent: true,
    },
    {
      label: t('layout.menu.supporters'),
      Icon: SupportersIcon,
      route: Routes.Supporters,
    },
  ];

  return (
    <div
      className="bg-primary h-mobile-header sticky top-0 z-[80] flex w-full justify-center"
      style={{ position: 'sticky' }}
    >
      <div className="flex w-full items-center px-4">
        <nav className="mx-auto flex w-full max-w-[400px] items-center justify-between">
          {navItems.map(({ label, Icon, route, accent }) => (
            <AppLink
              key={route}
              className="flex flex-col items-center"
              href={route}
              aria-label={label}
            >
              <Icon
                className={cn(
                  'w-6 hover:cursor-pointer hover:opacity-80',
                  accent ? 'text-accent-1' : 'text-white',
                )}
              />
              <p className="text-neutral-white text-menu pt-1">{label}</p>
            </AppLink>
          ))}
          <button
            aria-label={t('layout.menu.more')}
            onClick={() => slideMenuToggle(true)}
            className="flex flex-col items-center"
          >
            <MoreIcon className="w-6 text-white hover:cursor-pointer hover:opacity-80" />
            <p className="text-neutral-white text-menu pt-1">
              {t('layout.menu.more')}
            </p>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default MobileHeader;
